<script lang="ts">
	//Svelte hooks
	import { afterUpdate, beforeUpdate, onDestroy, onMount, setContext, tick } from 'svelte';

	// Svelte store
	import { writable, get } from 'svelte/store';

	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from 'src/services/http';

	//Configs
	import { devices_config } from '@devices/index';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	import { get_wave_device_settings } from 'src/helpers/z_wave_settings_config';

	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		categoriesWithoutEventLogDrawerTab,
		categoriesWithoutNotificationsDrawerTab,
		get_gen_2_component_channel_structure,
		get_mode,
		is_gen_2,
		load_image_from,
		real_id,
		get_local_ip,
		is_gen_z,
		isProThreeEM,
		isWallDisplay,
		is_dali,
		isGen2EM,
		isBluTRV,
		is_loqed,
		isWeatherStation,
		isDiffuser,
		isTRV,
		isPill,
		isCameraDevice,
		isDistance,
		isSecondaryWallDisplay,
		isActuator as isActuatorHelper,
		isSmokeLTE,
		isCctOnlyDevice,
		isXMODDevice,
		lightDevicesWithoutModeSwitch,
		isNoRelayPlug,
		isRgbwwPm,
		isFanDevice,
		isBluTRVGateway,
	} from 'src/helpers/interface_helpers';
	import { checkIsCriticalEvent, status_configurator } from 'src/helpers/device_data_helpers';
	import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
	import {
		isNightModeEnabled,
		isDeviceOff,
		supportsColorModeChange,
	} from 'src/helpers/night_mode_helpers';
	import { isRgbCct } from 'src/helpers/interface_helpers';
	import { hasExternalSensors } from 'src/helpers/ext_sensor_helpers';
	import { getAddonStatus, hasAddonPeripherals } from 'src/helpers/addons/addon_helpers';
	import { isSupportedVersion } from 'src/helpers/inclusion_helpers';
	import { minisStatisticsForceUpdateCheck } from 'src/helpers/firmware_speciffic_conditions';
	import { checkDeviceForceUpdate, getDeviceMinFwVersion } from 'src/helpers/firmware_helpers';
	import { getSettings, getStatus } from 'src/helpers/get_status_settings_helpers';
	import { roller_is_calibrated } from 'src/helpers/requests/calibrate_cover';
	import {
		buildNotification,
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';

	//Stores
	import { user_settings } from 'src/stores/account/user';
	import { firmwares, firmwares_gen_2 } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { active_integrations } from 'src/stores/integrations/alexa';
	import { devices } from 'src/stores/lists/devices';
	import { app, os } from 'src/stores/session/native-bridge';
	import { defaultStateType, getDeviceState } from 'src/stores/device-runtime/states';
	import { additional_notifications } from 'src/stores/features/device-notifications';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';

	//Design system components
	import {
		DrawerTabs,
		DrawerTab,
		DrawerTabContent,
		LabelList,
		ControlsContainer,
		ParameterCard,
		Loader,
		IconButton,
		Alert,
		Label,
		toast,
		CircleAvatar,
		Text,
	} from '@Shelly/shelly-design-system';

	//Components
	import ControlOnOff from '../Controls/Single/ControlOnOff.svelte';
	import ControlLock from '../Controls/Single/ControlLock.svelte';
	import ControlPosition from '../Controls/Single/ControlPosition.svelte';
	import ControlSlider from '../Controls/Single/ControlSlider.svelte';
	import ControlColor from '../Controls/Single/ControlColor.svelte';
	import ControlDirectionDevicePage from '../Controls/Single/ControlDirectionDevicePage.svelte';
	import ControlTrvDevicePage from '../Controls/Single/ControlTrvDevicePage.svelte';
	import ControlTemp from '../Controls/Single/ControlTemp.svelte';
	import ControlMode from '../Controls/Single/ControlMode.svelte';
	import ControlMute from '../Controls/Single/ControlMute.svelte';
	import DeviceSettingsWrapper from '../DevicesSettings/DeviceSettingsWrapper.svelte';
	import EmeterDataG1 from '../EmeterData/EmeterDataG1.svelte';
	import EmeterDataG2 from '../EmeterData/EmeterDataG2.svelte';
	import TimerRemaining from '../TimerRemaining/TimerRemaining.svelte';
	import AddonPeripheralsDrawer from './AddonPeripheralsDrawer.svelte';
	import LastReporter from './LastReporter.svelte';
	import EditDeviceWizard from '../Devices/DeviceWizard/EditDeviceWizard.svelte';
	// import NotificationList from '../Lists/NotificationList.svelte';
	import DeviceChartsLayer from '../Chart/LayerChart/DeviceChartsLayer.svelte';
	import ThumbLayerChart from '../Chart/LayerChart/ThumbLayerChart.svelte';
	import EvChargerLiveChart from '../XT1/EvCharger/EvChargerLiveChart.svelte';
	import EvChargerLivetimeEnergy from '../XT1/EvCharger/EvChargerLivetimeEnergy.svelte';
	import InputsReaderVisualizer from '../Controls/Indicators/InputsReaderVisualizer.svelte';
	import ControlTrvBoost from '../Controls/Single/ControlTrvBoost.svelte';
	import EventLog from '../EventLog/EventLog.svelte';
	import ControlGasSensorMute from '../Controls/Single/ControlGasSensorMute.svelte';
	import ControlOnOffAddon from '../Controls/Single/ControlOnOffAddon.svelte';
	import ControlOnOffProSensorSwitch from '../Controls/Single/ControlOnOffProSensorSwitch.svelte';
	import ControlVentoraBoost from '../Controls/Single/ControlVentoraBoost.svelte';
	import ControlVentoraFanSpeed from '../Controls/Single/ControlVentoraFanSpeed.svelte';
	// import PairAndTroubleShoot from '../DevicesSettings/SettingsGBLE/PairAndTroubleShoot.svelte';
	import EmeterDataG2Phases from '../EmeterData/EmeterDataG2Phases.svelte';
	import ControlPositionG2 from '../Controls/Single/ControlPositionG2.svelte';
	import ControlDirectionDevicePageG2 from '../Controls/Single/ControlDirectionDevicePageG2.svelte';
	import ControlEffects from '../Controls/Single/ControlEffects.svelte';
	import NotificationsPanel from '../NotificationsPanel.svelte';
	import UpdateRequiredStatistics from '../Alerts/UpdateRequiredStatistics.svelte';
	import MandatoryFirmwareUpdateAlert from '../Alerts/MandatoryFirmwareUpdateAlert.svelte';
	import ThermostatsG2 from '../DevicesSettings/Settings/SettingsG2/ThermostatsWallDisplay/ThermostatsG2.svelte';
	import DeviceSettingsNewFw from '../DevicesSettings/NewFirmwareSuggestionAlert.svelte';
	import VirtualDeviceDrawer from '../DevicesSettings/VirtualComponents/VirtualDeviceDrawer.svelte';
	import BluTrvMainControl from '../DevicesSettings/VirtualComponents/BluTRV/BluTrvMainControl.svelte';
	import PeakPowerInfo from '../Consumption/PeakPowerInfo.svelte';
	import DeviceStateView from './DeviceDrawer/DeviceStateView.svelte';
	import TemplatesDeviceDrawer from 'src/components/XT1/TemplatesDeviceDrawer.svelte';
	import PeripheralDeviceDrawer from '../DevicesSettings/AddonSettingsG2/PeripheralDevice/PeripheralDeviceDrawer.svelte';
	import WallDisplayVd from '../DevicesSettings/VirtualComponents/WallDisplay/WallDisplayVD.svelte';
	import TemplatesModal from '../Templates/TemplatesModal.svelte';
	import FireGroupList from './DeviceDrawer/FireGroupList.svelte';
	// import TransitionWrapper from '../Utility/TransitionWrapper.svelte';
	//Types
	import type { ChartConfigInterface } from 'src/types/Chart';
	import type { ActiveDeviceDrawer, DeviceDrawerTabItem } from 'src/types/Drawers';
	//Version comparison
	import { compare, validate } from 'compare-versions';

	//Constants
	import {
		ALEXA_FFS_TYPES,
		DIMMERS_WITH_CALIBRATION,
		SUGGESTED_FW_UPDATE,
		WALL_DISPLAYS_WITHOUT_INTERNAL_SENSOR,
	} from 'src/constants';

	//SPA Router
	import { push, location } from 'svelte-spa-router';
	import EnergyDistributionZendure from '../Integrations/Zendure/EnergyDistributionZendure.svelte';
	import PromoAlert from '../Promotions/PromoAlert.svelte';
	import GatewayG3MainControl from '../DevicesSettings/VirtualComponents/BluTRV/GatewayG3MainControl.svelte';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';

	import LastEvents from '../EventLog/LastEvents.svelte';
	import MediaPlayerComponent from '../MediaPlayerComponent.svelte';
	import PendingSleepAlert from '../Alerts/PendingSleepAlert.svelte';
	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';
	import { isXT1ComponentDevice, isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import BluetoothSettings from '../DevicesSettings/SettingsGBLE/BluetoothSettings.svelte';
	import isDimmerWithCalibration from 'src/helpers/is_calibratable_dimmer';
	import WeatherStationDevice from '../WeatherStation/WeatherStationDevice.svelte';
	import ControlAmbientCury from '../Controls/Single/ControlAmbientCury.svelte';
	import { isSubAccount, hasSettingsRights } from 'src/helpers/user_helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import RadarDrawer from '../Radar/RadarDrawer.svelte';
	import { isDeviceTemplatesSupported } from 'src/helpers/settings/settings_config_helpers';
	import ControlCuryDrawer from '../Controls/Single/ControlCuryDrawer.svelte';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import PillDeviceDrawer from '../ThePill/PillDeviceDrawer.svelte';
	import EvChargeEventLog from '../XT1/EvCharger/EventLog/EvChargeEventLog.svelte';
	import CameraDeviceDrawer from '../Camera/DeviceDrawer/CameraDeviceDrawer.svelte';
	import CameraRecordingsComponent from '../Camera/DeviceDrawer/RecordingsTab/CameraRecordingsComponent.svelte';
	import CameraStreamPreview from '../Camera/DeviceDrawer/CameraSettings/CameraStreamPreview.svelte';
	import { requestEditDevice } from '../Lists/list-helpers';
	import DistanceMainTab from '../Distance/DistanceMainTab.svelte';
	import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	let id = $sidePanelStateCombined?.id;
	let device = $devices[id];
	let gen2 = is_gen_2(device);
	let isSplit = device?.is_split;
	$: image = device?.image;
	let status, settings;
	$: statusStore = getDeviceStatus(device?.id);
	$: settingsStore = getDeviceSettings(device?.id);
	let openedEventTimestamp = null;
	let deviceState: any = getDeviceState(id);

	let isRadar = devices_config?.[device?.type]?.isRadar || false;

	$: {
		if ($devices[id]) {
			device = $devices[id];
		} else {
			device = undefined;
		}

		if (device) {
			status = $statusStore;
			settings = $settingsStore;

			if (device.hideCharts) {
				const tempStatus = status?.['temperature:0'];
				if (
					WALL_DISPLAYS_WITHOUT_INTERNAL_SENSOR.includes(device.type) &&
					tempStatus?.tC > 0 &&
					tempStatus?.tF > 0
				) {
					delete device.hideCharts;
					requestEditDevice(device);
				}
			}
		}
	}

	let loadingStateWhenPending = false;

	let editDevice = $devices[id];
	$: if (editDevice?.name !== $devices[id]?.name) {
		editDevice = { ...editDevice, name: $devices[id]?.name };
	}

	let mode = '';

	let deviceConfig = devices_config?.[device?.type];
	let isDotShowed = false;
	let isDotUpdated = false;

	let activeDevice: ActiveDeviceDrawer;

	$: deviceName = $devices?.[id]?.name;

	let activeDeviceTabIndex = 0;
	let scrollToActiveElID = null;

	let timerLabels: Record<string, any> = {};
	let labelList: any[] = [];
	let mainLabel;
	let _statuses = [];

	// Make labelList reactive to status, device, and settings changes
	$: if (status?.online && typeof deviceConfig !== 'undefined') {
		_statuses = status_configurator(status, device, settings);
		const defaultLabelList = _statuses.filter((e) => {
			// Filter out main labes and 'state' label (will be shown as ParameterCard)
			return (
				((e.main === undefined || e.name === 'distance') && isDistance(device.type)) ||
				(e.main === undefined && e.name !== 'state') ||
				isWeatherStation(device.type)
			);
		});
		// Honor the user's saved order/visibility from the Customize Device
		// wizard when available; otherwise fall back to the default list.
		labelList = device?.statusLabelParam
			? getCustomizeLabelList(device, _statuses)
			: defaultLabelList;
		mainLabel =
			!isWeatherStation(device.type) &&
			_statuses.filter((e) => {
				// Hack: show 'state' label as a main one
				return e.main !== undefined || e.name === 'state';
			})[0];
	}

	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	$: secondaryControlsDisabled =
		status?.online && settings
			? isDeviceOff(device, status) &&
				isNightModeEnabled(device, settings) &&
				(supportsColorModeChange(device.type) || isRgbCct(device.type))
			: false;

	let deviceDrawerContainer = writable(null);

	setContext('deviceDrawerContainer', deviceDrawerContainer);

	let categoriesWithoutEventLogDrawerTabCustom = [...categoriesWithoutEventLogDrawerTab];
	//tab notification based on device notifications
	$: notifier =
		$additional_notifications?.[id]?.length > 0 ||
		Object.keys(getNotificationsForDevice(device?.id)).length > 0;

	const deviceChartsConfig: ChartConfigInterface[] = getDeviceChartsConfig(device),
		testUser = isDevServerUser(['repo', 'nb']) || isT1User();

	$: chartTab = activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'charts';
	//adding optionals because drawerTabItems is undefined at the beginning

	$: shareTab = activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'share';
	//adding optionals because drawerTabItems is undefined at the beginning

	$: appSettingsTab =
		activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'application_settings'; //adding optionals because drawerTabItems is undefined at the beginning

	$: notificationsTab =
		activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'notifications'; //adding optionals because drawerTabItems is undefined at the beginning

	$: deviceInformationTab =
		activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'device_information'; //adding optionals because drawerTabItems is undefined at the beginning

	$: eventsLogTab = activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'event_log';

	$: thermostatTab = activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'thermostat'; //adding optionals because drawerTabItems is undefined at the beginning
	$: mediaPlayerTab = activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'media_player'; //adding optionals because drawerTabItems is undefined at the beginning

	$: cameraRecordingsTab =
		activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'camera_recordings'; //adding optionals because drawerTabItems is undefined at the beginning
	$: safetyTab = activeDevice?.drawerTabItems?.[activeDeviceTabIndex]?.title === 'safety'; //adding optionals because drawerTabItems is undefined at the beginning
	$: currentMode = device?.id ? get_mode(device, status, settings) : null;
	$: shouldRenderEventsLogTab =
		!categoriesWithoutEventLogDrawerTabCustom.includes(device?.category) &&
		!isBluTRV(device?.type) &&
		!(isPeripheralDevice(device?.type) && device?.category != 'relay') &&
		!(device?.category === 'module' && currentMode === 'shell');

	$: if (!eventsLogTab) {
		openedEventTimestamp = null;
	}

	let sliderProp = '';
	let hasColorTempControl = false;
	let hasColorControl = false;
	let hasLightControl = false;
	let hasEffects = false;
	let hasWhiteSlider = false;
	let selectedEffect;
	let effectLabel;
	let showModalEdit = false;
	let showModalEditFromNotification = false;
	let showTemplatesModal = false;

	const saveSettingsAlertState = async (deviceId: string) => {
		const device = $devices[deviceId];
		if (device) {
			try {
				const updatedDevice = {
					...device,
					settingsAlertHandled: true,
				};

				const res = await postReq('interface/device/save', {
					id: deviceId,
					data: JSON.stringify(updatedDevice),
				});

				if (res?.isok) {
					devices.update(updatedDevice);
				} else {
					console.error('Failed to save settings alert state:', res);
				}
			} catch (error) {
				console.error('Error saving settings alert state:', error);
			}
		}
	};

	$: if (typeof status?.online !== 'undefined' && status?.online) {
		selectedEffect = status?.lights ? status.lights[device.channel]?.effect : 0;
		effectLabel = devices_config[device?.type]?.modes?.color?.effects[selectedEffect]?.label;
	}
	let rollerCalibrated = false;
	let rollerLostPosition = false;
	let positioningControl = false;

	let calibrating = false;
	let fwUpdateing = false;

	let gen_1_timer;
	let gen_2_timer;
	let emeter_timer;
	let renderExtraTabs; //using this to prevent reactive checks for device type based rendering of extra tabs
	let isDrawerTabClicked = false;
	let initialWallDisplayDrawerTabsState;
	let secondaryWallDisplay = isSecondaryWallDisplay(device);
	$: copyDrawerTabs = activeDevice?.drawerTabItems;
	$: description = $deviceState?.description?.value || undefined;
	$: isWallDisplayVD = isWallDisplay(device?.type) && device?.category !== 'relay';
	$: if (description) {
		initialWallDisplayDrawerTabsState = false;
		activeDeviceTabIndex = 0;
	}

	$: if (
		isWallDisplay(device?.type) &&
		typeof settings?.DeviceInfo?.ver !== 'undefined' &&
		compare(settings?.DeviceInfo?.ver.match(/(\d{1,}\.){1,}\d{1,}/)[0], '1.2.5', '>=') &&
		activeDeviceTabIndex &&
		!initialWallDisplayDrawerTabsState &&
		typeof initialWallDisplayDrawerTabsState === 'boolean'
	) {
		if (activeDevice?.drawerTabItems.length !== copyDrawerTabs.length) {
			if (activeDevice?.drawerTabItems.find((item) => item.title === 'thermostat')) {
				if (isDrawerTabClicked) activeDeviceTabIndex += 1;
			} else {
				if (isDrawerTabClicked) activeDeviceTabIndex -= 1;
			}
			initialWallDisplayDrawerTabsState = true;
			isDrawerTabClicked = false;
		}
	}

	$: isCalibrating = $deviceState?.type === 4; // pro dimmer & roller 2.5/cover pro2pm
	$: isRebooting = $deviceState?.type === 2;
	$: isUpdating = $deviceState?.type === 3;
	let isActuator = isActuatorHelper(settings, device);

	const isGen2 = is_gen_2(device);

	const isDali = is_dali(device);
	const isLoqed = is_loqed(device);
	$: noControlGearDevices = isDali ? status?.dali?.cg_count === 0 : false;
	$: daliErrors = isDali ? status?.dali?.errors?.length > 0 : undefined;

	// isGen2 is checking for gen 2 and above
	// so we can use it for  gen 3
	const fwVersion = isGen2 ? settings?.sys?.device.fw_id : settings?.fw;
	const minVersion = isGen2
		? $firmwares_gen_2[device.id]?.stable?.version
		: $firmwares?.[device?.type]?.version;

	const hasConstantMinVersion = isGen2 ? SUGGESTED_FW_UPDATE[device.type] : undefined;

	// $: hasHtPromotion =
	// 	typeof $user_settings?.ht_codes?.[device.id] !== 'undefined' &&
	// 	typeof $user_settings?.ht_codes?.[device.id]?.dismissed === 'undefined' &&
	// 	status?.sys?.batch_date < 240226;
	// $: hasPromotionLimit =
	// 	status?.online &&
	// 	isWallDisplay(device?.type) &&
	// 	typeof $user_settings?.ht_codes?.[device.id] === 'undefined' &&
	// 	status?.sys?.batch_date < 240226; //Wall display campaign user has more than 7 devices
	$: wallDisplayUpdateNeeded =
		isWallDisplay(device?.type) && typeof status?.sys?.batch_date === 'undefined';

	let _isDimmerWithCalibration = isDimmerWithCalibration(device.type);
	let calibratingWasStarted = false;

	$: if (isCalibrating) {
		calibratingWasStarted = true;
	} else if (calibratingWasStarted && !isCalibrating) {
		calibratingWasStarted = false;
		if (status?.[get_gen_2_component_channel_structure(device)]?.errors?.length > 0) {
			status?.[get_gen_2_component_channel_structure(device)]?.errors.forEach((error) => {
				const errorObject = buildNotification(error, device, status, settings);
				toast.open({
					type: 'danger',
					title: $format('cal_error_detected', {
						default: `Calibration problem detected`,
					}),
					text: errorObject?.message,
				});
			});
		}
	}

	function shouldUpdateCurrentVersion(minFirmwareVersion, currentVersion) {
		if (
			!minFirmwareVersion ||
			!currentVersion ||
			!validate(minFirmwareVersion) ||
			!validate(currentVersion)
		) {
			return;
		}

		const numOfCurrent = currentVersion.split('-')[0];

		return compare(minFirmwareVersion, numOfCurrent, '>');
	}

	let shouldUpdateConstantFirmwareVersion = shouldUpdateCurrentVersion(
		hasConstantMinVersion,
		fwVersion?.split('/')[1],
	);

	const filterSettings = (settings) => {
		// Guard against undefined or non-array input
		if (!settings || !Array.isArray(settings)) {
			return [];
		}

		const external_sensors = settings?.find(
			(item: { title: string }) => item.title == 'external_sensors',
		);

		if (external_sensors) {
			if (!hasExternalSensors(device)) {
				settings = settings.filter(
					(item: { title: string }) =>
						!/external_sensors|temperature_automation|humidity_automation|sensor_actions/.test(
							item.title,
						),
				);
			} else if (!hasExternalSensors(device, 'humidity')) {
				settings = settings.filter(
					(item: { title: string }) => !/humidity_automation/.test(item.title),
				);
			}
		}

		settings = settings
			.map(
				(item: {
					title: string;
					disabled: boolean;
					warning: boolean;
					test_user: boolean;
					components: any;
				}) => {
					// set enable/disable settings tabs for shared devices
					item.disabled =
						device.shared &&
						!/^(application_settings|share|settings|device_information)$/.test(item.title);

					// FE-2007: keep the share tab visible on shared homes but disabled,
					// since sharing is managed by the home owner.
					if (isActiveHomeShared($homes.list) && item.title === 'share') {
						item.disabled = true;
					}

					// if (device.type === 'SAWD-0A1XX10EU1' && /actions/.test(item.title)) {
					// 	// set enabled/disabled tabs for wall dispaly
					// 	item.disabled = true;
					// }

					if (isGen2 && /favorites/.test(item.title)) {
						// temporary disable the fav. positions tab for gne 2 cover devices
						item.disabled = true;
					}

					if (item.title != 'settings' && $deviceState?.type === 14) {
						item.disabled = true;
					}

					if (shouldUpdateConstantFirmwareVersion && item.title === 'settings') {
						item.warning = true;
					}
					// remove test user tabs for non test users
					if (item.test_user && !testUser) {
						item.disabled = true;
					}

					// disable application_settings tab for shared devices if there is no active settings
					if (/application_settings/.test(item.title) && device.shared) {
						item.disabled = !item?.components?.some((component) =>
							/^(pin_lock|device_card_customization)$/.test(component.title),
						);
					}

					// Show device notifications tab for T1 test user
					// by deleting the test_user property
					// if (/application_settings/.test(item.title)) {
					// 	item.components = item.components.map((component: any) => {
					// 		if (
					// 			/device_notifications/.test(component.title) &&
					// 			$user_data.test_user_t1
					// 		) {
					// 			delete component.test_user;
					// 		}
					// 		return component;
					// 	});
					// }
					return item;
				},
			)
			.filter((setting) => {
				if (isGen2) return setting.title !== 'favorites';
				return true;
			});

		// Peripheral devices: application_settings only in SidePanel (settings modal), not in drawer
		if (device?.type === 'peripheral-device') {
			settings = settings.filter((setting) => setting.title !== 'application_settings');
		}

		return settings;
	};

	$: rerenderDrawerTabContent = activeDeviceTabIndex + $deviceState?.type;

	$: if (
		Object.keys($routeToDeviceSettings)?.length > 0 &&
		Object.keys($routeToDeviceSettings)[0] === device?.id &&
		typeof activeDevice?.drawerTabItems !== 'undefined'
	) {
		if (isWallDisplay(device?.type) && !isWallDisplayVD && !secondaryWallDisplay) {
			renderExtraTabs = true;
			renderTabs();
		}

		if (isCameraDevice(device)) {
			renderExtraTabs = true;
			renderTabs();
		}

		//routing to setting's tab passed from notification list(maybe can use other sources)
		const component = $routeToDeviceSettings[device.id].setting;
		const index = activeDevice.drawerTabItems.findIndex((i) => i.title == component);
		activeDeviceTabIndex = index >= 0 ? index : activeDevice.drawerTabItems.length - 1;
		if (!$routeToDeviceSettings[device.id]?.subSetting) {
			//Reset the route after opening the desired tab if no subsettings exist
			routeToDeviceSettings.resetTarget();
		}
	}

	$: ($deviceState, $homes.list, renderTabs()); //using device state to rerender drawer tabs and settings

	$: notificationsToShow = getNotificationsForDevice(device?.id);
	$: criticalNotifications = getCriticalNotifications(notificationsToShow);

	$: if (isSubAccount() && showModalEdit) {
		showModalEdit = false;
	}

	onMount(async () => {
		await tick();

		if ($deviceState?.type == 1) {
			// if pending connection
			loadingStateWhenPending = true;
			await getSettings(device);
			await getStatus(device);
			settings = settings;
			loadingStateWhenPending = false;
		}

		if (
			(isWallDisplay(device?.type) &&
				!isWallDisplayVD &&
				!secondaryWallDisplay &&
				typeof settings?.DeviceInfo?.ver !== 'undefined' &&
				compare(settings?.DeviceInfo?.ver.match(/(\d{1,}\.){1,}\d{1,}/)[0], '1.2.5', '>=')) ||
			is_gen_z(device)
		) {
			//getting to know if we need to render extra tabs
			renderExtraTabs = true;
		}

		if (isCameraDevice(device)) {
			renderExtraTabs = true;
		}

		if (
			device.category === 'module' &&
			categoriesWithoutEventLogDrawerTabCustom.includes(device.category) &&
			currentMode !== 'shell'
		) {
			categoriesWithoutEventLogDrawerTabCustom = categoriesWithoutEventLogDrawerTabCustom.filter(
				(cat) => cat !== 'module',
			);
		}

		renderTabs(); //rendering settings and tabs

		if (device.gen !== 'GBLE' && get_local_ip(device.id)) {
			let owned_res = await postReq('/device/is_available', {
				id: real_id(id),
			});

			if (owned_res?.isok) {
				//@ts-ignore
				if (!owned_res?.data?.is_owner) {
					// Add to the additional notifications store
					additional_notifications.add(id, 'associateCloud');
					notifier = true;
				} else {
					additional_notifications.remove(id, 'associateCloud');
				}
			} else {
				console.warn('Warning: Device is not available', owned_res.errors);
			}
		}
	});

	const navigateToEventLog = (event) => {
		const eventLogIndex = activeDevice?.drawerTabItems.findIndex((el) => el.title === 'event_log');

		activeDeviceTabIndex = eventLogIndex;
		openedEventTimestamp = event?.originalTimestamp;
	};

	async function renderTabs() {
		//getting mode and rendering tabs
		activeDevice = {
			name: device?.name,
			drawerTabItems: [
				{
					title: 'main_controls',
					image: image
						? load_image_from(
								device?.image,
								device?.localImage ? '' : device?.server,
							).replace('thumb_', '')
						: undefined,
					backgroundColor: device?.backgroundColor,
					disabled: false,
					warning: false,
				},
			],
		};

		mode = get_mode(device, status, settings);

		if (renderExtraTabs) {
			if (
				isWallDisplay(device?.type) &&
				!secondaryWallDisplay &&
				typeof settings?.DeviceInfo?.ver !== 'undefined' &&
				compare(settings?.DeviceInfo?.ver.match(/(\d{1,}\.){1,}\d{1,}/)[0], '1.2.5', '>=') &&
				typeof settings?.['thermostat:0'] !== 'undefined'
			) {
				//adding thermostat tab after 1.2.5 fw version Wall Display. New rule if thermostat is enabled, else thermostat tab is not rendered
				activeDevice.drawerTabItems.push({
					title: 'thermostat',
					disabled: false,
				});
			}

			if (
				isWallDisplay(device?.type) &&
				!secondaryWallDisplay &&
				typeof settings?.DeviceInfo?.ver !== 'undefined' &&
				compare(settings?.DeviceInfo?.ver.match(/(\d{1,}\.){1,}\d{1,}/)[0], '1.2.5', '>=') &&
				device?.media_player_enabled
			) {
				activeDevice.drawerTabItems.push({
					title: 'media_player',
					image: null,
					components: [null],
					disabled: device.shared,
				});
			}

			if (is_gen_z(device)) {
				//if device is gen z rendering settings from another config
				if (typeof settings?.['Configuration:0'] !== 'undefined') {
					const waveSettings = get_wave_device_settings(
						settings?.['Configuration:0'],
						device.type,
					);
					const filteredWaveSettings = filterSettings(waveSettings);

					activeDevice.drawerTabItems = [
						...(activeDevice.drawerTabItems || []),
						...(Array.isArray(filteredWaveSettings) ? filteredWaveSettings : []),
					];
				}
			}
		}

		const hasColorTempControlForTabs =
			isXMODDevice(device?.type) || isXT1ComponentDevice(device)
				? status?.['cct:0'] && device?.mode === 'cct'
				: devices_config?.[device?.type]?.modes?.[mode]?.color_temp;
		const hasColorControlForTabs =
			isXMODDevice(device?.type) || isXT1ComponentDevice(device)
				? status?.['rgbw:0'] && device?.mode === 'rgbw'
				: devices_config?.[device?.type]?.modes?.[mode]?.color;

		const shouldShowSecondaryControls =
			(!isRgbwwPm(device?.type) &&
				device?.category === 'light' &&
				(hasColorTempControlForTabs || hasColorControlForTabs)) ||
			isCctOnlyDevice(device?.type) ||
			device?.mode === 'rgb' ||
			device?.mode === 'rgbw' ||
			device?.mode === 'cct';

		if (shouldShowSecondaryControls) {
			activeDevice.drawerTabItems.push({
				title: 'secondary_controls',
				disabled: false,
				warning: false,
			});
		}
		if (isDiffuser(device?.type)) {
			activeDevice.drawerTabItems.push({
				title: 'ambient_light',
				disabled: false,
				warning: false,
			});
		}

		if (isCameraDevice(device)) {
			activeDevice.drawerTabItems.push({
				title: 'camera_recordings',
				disabled: device?.shared,
				warning: false,
			});
		}

		if (!categoriesWithoutNotificationsDrawerTab.includes(device?.category)) {
			activeDevice.drawerTabItems.push({
				title: 'notifications',
				disabled: false,
				warning: notifier,
			});
		}

		if (
			(deviceChartsConfig.length && !secondaryWallDisplay) ||
			($deviceState?.type === 15 && device?.category === 'sensor')
		) {
			activeDevice.drawerTabItems.push({
				title: 'charts',
				disabled: $deviceState?.type === 14 || $deviceState?.type === 15,
				warning: false,
			});
		}

		// activeDevice.drawerTabItems.push({
		// 	title: 'notifications',
		// 	disabled: device.shared,
		// 	notification: true,
		// 	warning: false
		// });

		if (shouldRenderEventsLogTab) {
			activeDevice.drawerTabItems.push({
				title: 'event_log',
				disabled: device?.shared || $deviceState?.type === 14,
				warning: false,
			});
		}

		const deviceModes = deviceConfig?.modes || {};

		let settingsMode;
		if (deviceModes?.[mode]?.settings) {
			settingsMode = mode;
		} else if (device?.mode && deviceModes?.[device.mode]?.settings) {
			settingsMode = device.mode;
		} else {
			settingsMode = Object.keys(deviceModes)[0];
		}

		const modeSettings = deviceModes?.[settingsMode]?.settings;

		if (modeSettings) {
			const filteredModeSettings = filterSettings(modeSettings);
			if (secondaryWallDisplay) {
				// For secondary wall display channels, keep only a curated subset of settings
				const allowedTitles = new Set([
					'timer',
					'schedule',
					'actions',
					'share',
					'application_settings',
					'settings',
				]);
				const subset = (Array.isArray(filteredModeSettings) ? filteredModeSettings : []).filter(
					(s) => allowedTitles.has(s?.title),
				);
				activeDevice.drawerTabItems = [...(activeDevice.drawerTabItems || []), ...subset];
			} else {
				activeDevice.drawerTabItems = [
					...(activeDevice.drawerTabItems || []),
					...(Array.isArray(filteredModeSettings) ? filteredModeSettings : []),
				];
			}
		}

		// if (typeof status !== 'undefined') {//generate settings tabs not based on status anymore
		// combine drawerTabs from device_config together with dynamic drawerTabs

		// console.warn(`activeDevice.drawerTabItems`, activeDevice.drawerTabItems);
		// }

		if (
			isProThreeEM(device?.type) &&
			!hasAddonPeripherals(device) &&
			!categoriesWithoutEventLogDrawerTabCustom.includes(device?.category)
		) {
			categoriesWithoutEventLogDrawerTabCustom.push(device.category);
		}
		if (
			isProThreeEM(device?.type) &&
			hasAddonPeripherals(device) &&
			categoriesWithoutEventLogDrawerTabCustom.includes(device?.category)
		) {
			categoriesWithoutEventLogDrawerTabCustom = categoriesWithoutEventLogDrawerTabCustom.filter(
				(el) => el !== device.category,
			);
		}

		const isWallDisplayThermostatEnabled =
			isWallDisplay(device?.type) &&
			Object.keys(settings ?? {}).some((key) => key.startsWith('thermostat'));

		const sensorSettingsIndex = activeDevice.drawerTabItems.findIndex(
			(item) => item.title === 'sensor_settings',
		);

		if (
			isTRV(device?.type) ||
			isBluTRV(device?.type) ||
			(isWallDisplay(device?.type) && !device.id.includes('_') && isWallDisplayThermostatEnabled)
		) {
			const sensorSettings = activeDevice.drawerTabItems.find(
				(item) => item.title === 'sensor_settings',
			);

			// Only modify if sensorSettings exists
			if (sensorSettings && sensorSettingsIndex !== -1) {
				const hasTempFavPositions = sensorSettings?.components?.some(
					(component) => component.title === 'temperature_favorite_positions',
				);
				if (!hasTempFavPositions) {
					activeDevice.drawerTabItems.splice(sensorSettingsIndex, 1, {
						...sensorSettings,
						components: [
							{
								title: 'temperature_favorite_positions',
								component: 'temperature_favorite_positions',
							},
							...(sensorSettings?.components || []),
						],
					});
				}
			}
		}

		// Add sensor settings tab for wall display devices (main device only)
		if (isWallDisplay(device?.type) && !device?.id?.includes('_')) {
			const hasSensorSettings = activeDevice.drawerTabItems.some(
				(item) => item.title === 'sensor_settings',
			);

			if (isWallDisplayThermostatEnabled && !hasSensorSettings) {
				// Add sensor settings tab for wall display main device
				activeDevice.drawerTabItems.splice(activeDevice.drawerTabItems.length - 6, 0, {
					title: 'sensor_settings',
					disabled: false,
					components: [
						{
							title: 'temperature_favorite_positions',
							component: 'temperature_favorite_positions',
						},
					],
				});
			} else if (!isWallDisplayThermostatEnabled && hasSensorSettings) {
				activeDevice.drawerTabItems.splice(sensorSettingsIndex, 1);
			}
		}
	}

	function updateNotificationTab(hasNotifier) {
		//find the index of the notification tab
		let removeItemIndex = activeDevice?.drawerTabItems.findIndex(
			(item) => item.title === 'notifications',
		);
		//replace the notification tab with a new one
		activeDevice.drawerTabItems[removeItemIndex] = {
			...activeDevice.drawerTabItems[removeItemIndex],
			warning: hasNotifier,
		};
	}

	function restrictSettingIfNoRights() {
		const allowedSettings = ['main_controls', 'notifications', 'charts', 'event_log'];

		activeDevice.drawerTabItems = activeDevice?.drawerTabItems.map((item) => {
			return {
				...item,
				disabled: !allowedSettings.includes(item.title),
			};
		});
	}

	afterUpdate(() => {
		if (!hasSettingsRights(device)) {
			restrictSettingIfNoRights();
		}

		//check if there is an notification. notifier return true if there is an notification for the current device.
		//notifier is an reactively updated variable
		//with isDotShowed checks if I already have been updated tab in the drawer
		if (notifier && !isDotShowed) {
			isDotShowed = true;
			isDotUpdated = false;

			updateNotificationTab(notifier);
		} else if (!notifier && !isDotUpdated) {
			//if there isn't notification and notification signal(the dot) is not updated
			//for example there was a notification and dot appears, but now
			// there isn't notification and we don't want dot anymore
			//then update the notification tab with new notification object without а dot
			isDotUpdated = true;
			isDotShowed = false;

			updateNotificationTab(notifier);
		}
	});

	beforeUpdate(() => {
		if (status?.online && typeof deviceConfig !== 'undefined') {
			if (!device) {
				return;
			}
			mode = get_mode(device, status, settings);

			if (isXMODDevice(device?.type) || isXT1ComponentDevice(device)) {
				hasColorControl = status?.['rgbw:0'] && device?.mode === 'rgbw';
				hasColorTempControl = status?.['cct:0'] && device?.mode === 'cct';
				hasLightControl = status?.['light:0'] && device?.mode === 'light';
				if (hasColorControl || hasColorTempControl || hasLightControl) {
					sliderProp = 'brightness';
					hasWhiteSlider = false;
				} else {
					sliderProp = '';
					hasWhiteSlider = false;
				}
			} else {
				hasColorTempControl = devices_config?.[device.type]?.modes?.[mode]?.color_temp;
				hasColorControl = devices_config?.[device.type]?.modes?.[mode]?.color; //this will visualize mode control if device has color channel inside device config
				hasWhiteSlider = devices_config?.[device.type]?.modes?.[mode]?.has_white;
				sliderProp = devices_config?.[device.type]?.modes?.[mode]?.slider;
			}
			hasEffects = !isGen2 ? status?.lights : false; //this is neeeded for gen 2++ bulbs not to show effects
			rollerCalibrated = roller_is_calibrated(device, status, settings);
			rollerLostPosition =
				status?.[`${get_gen_2_component_channel_structure(device)}`]?.current_pos === null;

			positioningControl =
				!gen2 && device.category === 'roller'
					? status.rollers?.[device.channel].positioning
					: true;

			if (device.category !== 'sensor') {
				emeter_timer =
					!is_gen_2(device) &&
					device.category === 'emeter' &&
					status?.['relays']?.[0].has_timer;
				gen_1_timer =
					!is_gen_2(device) &&
					device.category !== 'emeter' &&
					status?.[device.category + 's']?.[device.channel]?.has_timer;
				gen_2_timer =
					is_gen_2(device) &&
					typeof status?.[`${get_gen_2_component_channel_structure(device)}`]
						?.timer_started_at !== 'undefined' &&
					((settings?.[`${get_gen_2_component_channel_structure(device)}`]?.auto_on &&
						!status?.[`${get_gen_2_component_channel_structure(device)}`]?.output) ||
						(settings?.[`${get_gen_2_component_channel_structure(device)}`]?.auto_off &&
							status?.[`${get_gen_2_component_channel_structure(device)}`]?.output));
			}
		}
	});

	onDestroy(() => {
		console.log('ON DEVICE DRAWER DESTROY');
		// Alex: this never occures... we would be able to move most of the logic to onMount if we destory the drawer component on close instead of just moving it to the left...
	});

	// const shareIcon = device.shared
	// 	? 'custom-icon-shared-to-light'
	// 	: 'custom-icon-shared-by-light';

	//Use it in IconVariants when new icons arrived
	// 	share: shareIcon,
	enum IconVariants {
		secondary_controls = 'fa-palette',
		ambient_light = 'fa-lightbulb-on',
		effects = 'fa-stars',
		timer = 'fa-clock',
		schedule = 'fa-calendar',
		internet = 'fa-globe',
		networks = 'fa-globe',
		safety = 'fa-shield',
		actions = 'fa-link',
		webhooks = 'fa-link',
		night_mode = 'fa-moon',
		sensor_actions = 'fak fa-light-sensor-on-link',
		application_settings = 'fa-gears',
		fan_settings = 'fa-fan',
		sensor_settings = 'fak fa-light-sensor-on-gear',
		external_sensors = 'fak fa-light-sensor-on',
		plus_uni_counter = 'fa-gauge-circle-plus',
		virtual_components = 'fa-cube',
		bthome_components = 'fa-cube',
		irrigation_zones_settings = 'fa-droplet',
		irrigation_sequences = 'fa-network-wired',
		peripherals = 'fak fa-light-sensor-on',
		addon_sensors = 'fak fa-light-sensor-on',
		sensor_mute = 'fa-bell-slash',
		temperature_action = 'fa-temperature-arrow-down',
		humidity_action = 'fa-droplet-percent',
		adc_actions = 'fa-exchange',
		temperature_automation = 'fa-temperature-high',
		humidity_automation = 'fa-dewpoint',
		adc_automation = 'fa-tachometer-alt-fast',
		favorites = 'fa-star',
		scripts = 'fa-brackets-curly',
		share = 'fa-share-nodes',
		settings = 'fa-cog',
		charts = 'fa-chart-line',
		notifications = 'fa-bell',
		event_log = 'fa-notebook',
		valve = 'fa-pipe-valve',
		self_test = 'fa-clipboard-list-check',
		find_my_device = 'fa-radar',
		ui_settings = 'fa-sidebar-flip',
		thermostat = 'fa-temperature-list', //wall display's thermostat control
		media_player = 'fa-music', //wall display's media player control
		device_information = 'fa-circle-info',
		geo_location_and_elevation = 'fa-map-location-dot',
		pill_peripheral_settings = 'fa-gauge-circle-plus',
		camera_recordings = 'fa-clapperboard-play', //Camera recordings tab
		camera_zones = 'fa-square-dashed-circle-plus', //Camera zones tab
		camera_audio = 'fa-volume', //Camera audio settings tab
		camera_and_video = 'fa-video', //Camera & video settings tab
		camera_storage = 'fa-hard-drive', //Camera storage settings tab
		bluetooth = 'fa-bluetooth',
		tariff_configuration = 'fa-tags',
	}

	const getIconFromTitle = (title: string) => {
		if (title == 'main_controls' && device.icon && !device.image) {
			return device.icon;
		}
		let icon = IconVariants[title];
		if (icon == undefined) {
			console.warn('Missing icon for title:', title);
			return '';
		} else if (icon.includes('fak')) {
			return icon;
		} else if (icon.includes('fa-palette')) {
			return 'fa-light ' + icon;
		} else {
			return 'fal ' + icon;
		}
	};

	const getActiveIconDrawerTab = (icon: string) => {
		if (!icon) return undefined;

		const splittedIcon = icon.split(' ');

		if (splittedIcon[0] === 'fak') {
			return (
				icon
					.replace(/\bfa-sharp-(light|regular|thin)\b/, 'fa-sharp-solid')
					.replace(/\bfa-(light|regular|thin)\b/, 'fa-solid') + ' text-primary'
			);
		}

		return `fas ${splittedIcon[1]} text-primary`;
	};

	const getIconDrawerTab = (tabData, index) => {
		return tabData?.image
			? ''
			: activeDeviceTabIndex === index
				? getActiveIconDrawerTab(getIconFromTitle(tabData.title))
				: getIconFromTitle(tabData.title);
	};

	const handleDeviceEdit = () => {
		showModalEditFromNotification = false;
		showModalEdit = true;
	};

	const handleOnClickThumbChart = (e: CustomEvent | Event) => {
		const index = activeDevice.drawerTabItems.findIndex((i) => i.title == 'charts');
		scrollToActiveElID = null;
		if (index > 0) {
			if (e instanceof CustomEvent) {
				scrollToActiveElID = e.detail;
			}
			activeDeviceTabIndex = index;
		}
	};

	const getSubHeadingTitle = (drawerTabItems: DeviceDrawerTabItem[], activeDeviceTabIndex: number) => {
		const drawerTabItem = drawerTabItems?.[activeDeviceTabIndex];
		if (drawerTabItem?.title === 'charts' && device?.category === 'charger') {
			return $format('settings_title_insights', { default: 'Insights' });
		}
		if (drawerTabItem?.title === 'camera_recordings') {
			return $format('settings_title_camera_recordings', {
				default: 'Camera Recordings',
			});
		}
		return drawerTabItems
			? capFirstChar(
					$format('settings_title_' + drawerTabItem?.title, {
						default: drawerTabItem?.title?.replace('_', ' '),
					}),
				)
			: '';
	};

	let comingLocation = '';

	$: if (comingLocation && comingLocation !== $location) resetDrawerClose();
</script>

{#if device}
	<DrawerTabs
		bind:scrollContainerRef={$deviceDrawerContainer}
		withScrollable={activeDeviceTabIndex !== 0}
		backgroundImgSrc=""
	>
		<svelte:fragment slot="tabs">
			{#key activeDeviceTabIndex}
				{#if activeDevice.drawerTabItems}
					{#each activeDevice.drawerTabItems as drawerTab, index}
						<DrawerTab
							icon={!drawerTab?.backgroundColor && getIconDrawerTab(drawerTab, index)}
							image={drawerTab?.image}
							backgroundColor={drawerTab?.backgroundColor}
							active={activeDeviceTabIndex === index}
							notification={drawerTab.warning
								? 'danger'
								: drawerTab.notification
									? 'success'
									: undefined}
							on:click={() => {
								if (description) isDrawerTabClicked = true;
								scrollToActiveElID = null;
								activeDeviceTabIndex = index;
							}}
							disabled={($deviceState &&
								$deviceState?.type < defaultStateType &&
								!(checkDeviceForceUpdate(device) && status?.online) &&
								index !== 0 &&
								drawerTab.title !== 'charts' &&
								(!settings ||
									isRebooting ||
									isUpdating ||
									isCalibrating ||
									drawerTab.title !== 'networks') &&
								drawerTab.title !== 'settings' &&
								drawerTab.title !== 'share' &&
								drawerTab.title !== 'event_log' &&
								drawerTab.title !== 'application_settings' &&
								drawerTab.title !== 'notifications' &&
								drawerTab.title !== 'device_information' &&
								drawerTab.title !== 'camera_recordings') || //@ts.iliev: These checks have escalated beyond maintability.
								(device?.gen === 'GBLE' &&
									drawerTab.title === 'settings' &&
									(device?.shared ||
										!status?.online ||
										typeof settings === 'undefined')) ||
								//Alex: ToDo - we need to refactor this redundant check... those conditions are already checked in the filterSettings function
								drawerTab.disabled ||
								((calibrating || fwUpdateing) &&
									index !== 0 &&
									drawerTab.title !== 'charts')}
						/>
					{/each}
				{/if}
			{/key}
		</svelte:fragment>

		{#if $deviceState?.type < defaultStateType && !(checkDeviceForceUpdate(device) && status?.online) && !eventsLogTab && !chartTab && !shareTab && !appSettingsTab && !notificationsTab && !deviceInformationTab && !cameraRecordingsTab}
			<!-- Offline device, calibrating, firmware updating, other loading states-->
			<DrawerTabContent
				heading={deviceName}
				subHeading={getSubHeadingTitle(activeDevice.drawerTabItems, activeDeviceTabIndex)}
			>
				<svelte:fragment slot="headingButton">
					{#if !isSubAccount()}
						<IconButton
							aria-label={$format('button_edit_device', {
								default: 'Edit device',
							})}
							icon="fas fa-pen"
							on:click={handleDeviceEdit}
						/>
					{/if}
				</svelte:fragment>

				{#if activeDeviceTabIndex === 0}
					<PendingSleepAlert {device} />

					<!-- First Page -->
					<DeviceStateView {device} bind:status uiState={$deviceState} />
				{:else if activeDevice.drawerTabItems[activeDeviceTabIndex]?.title === 'settings'}
					{#if !$app && device?.gen === 'GBLE' && !device?.shared}
						<Alert
							type="warning"
							isHeadingBold
							heading={$format('alert_heading_ble_devices_restricted', {
								default: 'Bluetooth Restricted',
							})}
							message={$format('alert_message_ble_devices_restricted', {
								default:
									'Bluetooth devices can be configured only via the Shelly Smart Control application.',
							})}
							class="mb-m"
						/>
					{/if}
					<DeviceSettingsWrapper
						{device}
						tab={activeDevice.drawerTabItems[activeDeviceTabIndex]?.title}
						components={activeDevice.drawerTabItems[activeDeviceTabIndex].components}
					/>
				{:else if settings || isRebooting ? activeDevice.drawerTabItems[activeDeviceTabIndex]?.title === 'networks' : null}
					<DeviceSettingsWrapper
						{device}
						tab={activeDevice.drawerTabItems[activeDeviceTabIndex]?.title}
						components={activeDevice.drawerTabItems[activeDeviceTabIndex].components}
					/>
				{/if}
			</DrawerTabContent>
		{:else}
			{#key rerenderDrawerTabContent}
				<DrawerTabContent
					heading={deviceName}
					subHeading={getSubHeadingTitle(activeDevice.drawerTabItems, activeDeviceTabIndex)}
					withScrollable={activeDeviceTabIndex === 0}
				>
					<svelte:fragment slot="headingButton">
						{#if !isSubAccount()}
							<IconButton
								aria-label={$format('button_edit_device', {
									default: 'Edit device',
								})}
								slot="headingButton"
								icon="fas fa-pen"
								on:click={handleDeviceEdit}
							/>
						{/if}
					</svelte:fragment>
					{#if activeDeviceTabIndex === 0}
						<!-- Main controls, can be moved as a components array in the hardcoded config above -->

						{#if typeof device !== 'undefined' && typeof status !== 'undefined' && typeof settings !== 'undefined' && typeof deviceConfig !== 'undefined'}
							{#if checkDeviceForceUpdate(device)}
								<MandatoryFirmwareUpdateAlert
									{device}
									values={{
										fwVersion,
										minVersion: getDeviceMinFwVersion(device) ?? minVersion,
									}}
									isOffline={!status?.online}
								/>
							{/if}
							{#if device.category !== 'emeter' && device.category !== 'pv' && !isRadar}
								<div class="lable-list-wrapper-in-drawer-tab-content mb-s">
									{#if gen_1_timer}
										{#key status[device.category + 's'][device.channel].timer_started}
											<TimerRemaining
												timerKey="gen1_main"
												{device}
												{status}
												{settings}
												bind:timerLabels
											/>
										{/key}
									{/if}
									{#if gen_2_timer}
										{#key status[get_gen_2_component_channel_structure(device)].timer_started_at}
											<TimerRemaining
												timerKey="gen2_main"
												{device}
												{status}
												{settings}
												bind:timerLabels
											/>
										{/key}
									{/if}
									{#if !/^(virtual_device)$/.test(device?.category) && !isPeripheralDevice(device?.type) && !/^(S3XT-0S)$/.test(device?.type) && device?.mode !== 'media_player' && !isWeatherStation(device.type) && !isPill(device?.type)}
										<LabelList
											labelList={combinedLabelList}
											type="default"
											size="m"
											data-prevent-drawer-swipe
											data-prevent-sidepanel-swipe
											isDefaultCursor
											on:label-click={(e) => {
												e.stopPropagation();
											}}
										/>
									{/if}
								</div>

								{#if criticalNotifications?.length}
									<div class="d-flex justify-content-center mb-s max-width-full">
										<Label
											class="max-width-full"
											size="m"
											value={criticalNotifications[0].name}
											type={criticalNotifications[0].type === 'danger'
												? 'danger'
												: 'warning'}
											icon="fa-solid fa-shield-exclamation"
										/>
									</div>
								{/if}
							{/if}

							{#if isBluTRVGateway(device.type)}
								<GatewayG3MainControl {device} />
							{/if}
							{#if ALEXA_FFS_TYPES.includes(device?.type) && !$active_integrations.includes('alexa')}
								<Alert
									class="mb-s"
									heading={$format('alert_heading_alexa_ffs_device', {
										default: `Alexa compatible device`,
									})}
									message={$format('alert_message_alexa_ffs_device', {
										default:
											'This is a device that can be controlled by Alexa. To use it, you need to link your Shelly account with Alexa.',
									})}
									isHeadingBold
									cta={{
										title: capFirstChar(
											$format('link_go_to_alexa_integration', {
												default: 'Link with Alexa',
											}),
										),
									}}
									on:cta_click={() => {
										push('/settings/amazon_alexa_app_to_app?stage=prod');
										setSidePanelState({
											type: 'dashboard',
											id: '-1',
											open: false,
										});
									}}
								/>
							{/if}
							<!-- {#if (hasHtPromotion || hasPromotionLimit) && !isWallDisplayVD}
							<PromoAlert {device} bind:isLimit={hasPromotionLimit} />
						{/if} -->
							{#if (isDali && noControlGearDevices) || daliErrors}
								<div class="flex-full-height justify-content-end">
									<Alert
										class="mb-s"
										heading={$format('alert_heading_dali_control_gear_missing', {
											default: `${device.name} disconnected control gear.`,
											values: { deviceName: device.name },
										})}
										message={$format('alert_message_dali_control_gear_suggestion', {
											default:
												'There is no control gear connected to the Shelly device. Please check wirings and scan for control gear devices.',
										})}
										isHeadingBold
										cta={{
											title: $format('settings', { default: 'Settings' }),
										}}
										on:cta_click={() => {
											navigateToSetting(id, 'settings', 'dali_configuration');
										}}
									/>
								</div>
							{/if}
							{#if isXT1Module(device?.type)}
								<TemplatesDeviceDrawer
									{device}
									statuses={_statuses}
									on:click={handleOnClickThumbChart}
								/>
							{:else if isBluTRV(device.type)}
								<BluTrvMainControl {device} statuses={_statuses} />
							{:else if isVirtualDevice(device?.type)}
								<VirtualDeviceDrawer {device} statuses={_statuses} />
							{:else if isPill(device?.type)}
								<PillDeviceDrawer
									{device}
									statuses={_statuses}
									{status}
									{settings}
									{deviceChartsConfig}
									{handleOnClickThumbChart}
									on:click={handleOnClickThumbChart}
								/>
							{:else if isCameraDevice(device)}
								<CameraDeviceDrawer {device} statuses={_statuses} {status} {settings} />
							{:else if isPeripheralDevice(device?.type)}
								<PeripheralDeviceDrawer
									{device}
									statuses={_statuses}
									{deviceChartsConfig}
									{handleOnClickThumbChart}
								/>
							{:else if device?.gen === 'GBLE'}
								{#if isWeatherStation(device.type)}
									<WeatherStationDevice
										{device}
										statuses={_statuses}
										on:openChart={handleOnClickThumbChart}
									/>
								{:else if isDistance(device.type)}
									<DistanceMainTab
										{device}
										{deviceChartsConfig}
										{handleOnClickThumbChart}
									/>
								{/if}

								<div class="flex-full-height justify-content-end">
									{#if isWeatherStation(device.type)}
										{#if !device.shared}
											<LastReporter class="mb-xs" {device} />
										{/if}
									{:else if device.category === 'sensor' && !isRadar}
										<LastEvents
											{...!device.shared && { marginBottom: 'mb-m' }}
											{device}
											on:click={(e) => {
												if (!device.shared) {
													navigateToEventLog(e.detail);
												}
											}}
										/>

										{#if isSmokeLTE(device.type)}
											<FireGroupList {device} />
										{/if}

										<!-- adding last reporter for more gble devices -->
										{#if !device.shared}
											<LastReporter class="mb-s" {device} />
										{/if}

										{#if deviceChartsConfig.length && !isDistance(device.type)}
											<ThumbLayerChart
												class={mainLabel ? 'mb-s' : ''}
												{device}
												{deviceChartsConfig}
												on:click={handleOnClickThumbChart}
											/>
										{/if}

										{#if mainLabel && !isDistance(device.type)}
											<ParameterCard
												icon={mainLabel.icon}
												iconColor={mainLabel?.iconColor || ''}
												title={capFirstChar(mainLabel.value)}
												description={capFirstChar(mainLabel.translation || '')}
											/>
										{/if}
									{:else if device.category === 'inputs_reader' || device.category === 'remote_control'}
										<div class="flex-full-height" />
										<!-- {#if $user_data.test_user} -->
										<!-- <PairAndTroubleShoot {device} /> -->
										<!-- {/if} -->
										{#if !device.shared}
											<LastReporter class="mb-s" {device} />
										{/if}
										<ControlsContainer>
											<InputsReaderVisualizer
												slot="first"
												{device}
												{status}
												{settings}
												isWide
												isInCard={false}
											/>
										</ControlsContainer>
									{/if}
								</div>
							{:else if device.category === 'power_meter'}
								<div class="flex-full-height justify-content-end">
									{#if deviceChartsConfig.length}
										{#if minisStatisticsForceUpdateCheck(device, settings)}
											<UpdateRequiredStatistics {device} />
										{:else}
											<PeakPowerInfo {device} />
											<ThumbLayerChart
												{device}
												{deviceChartsConfig}
												on:click={handleOnClickThumbChart}
											/>
										{/if}
									{/if}
									{#if mainLabel}
										<ParameterCard
											icon={mainLabel.icon}
											iconColor={mainLabel?.iconColor || ''}
											title={mainLabel.value}
											description={capFirstChar(mainLabel.name)}
										/>
									{/if}
								</div>
							{:else if device.category === 'sensor'}
								{#if device.type === 'S4SN-0U61X'}
									<div class="flex-full-height justify-content-start">
										<RadarDrawer {device} {status} {settings} />
									</div>
								{/if}
								{#if !isWallDisplayVD && !isRadar}
									<div class="flex-full-height justify-content-end">
										{#if shouldUpdateConstantFirmwareVersion || wallDisplayUpdateNeeded}
											<div class="mb-s">
												<DeviceSettingsNewFw
													{device}
													values={{ fwVersion, minVersion }}
												/>
											</div>
										{/if}

										<LastEvents
											{device}
											on:click={(e) => {
												if (!device.shared) {
													navigateToEventLog(e.detail);
												}
											}}
										/>
										{#if device.type === 'SNSN-0031Z' || device.type === 'S4SN-1031Z' || device.type === 'SMSN-0031ZL'}
											<ControlMute {device} class={mainLabel ? 'mb-s' : ''} />
										{:else if deviceChartsConfig.length}
											<ThumbLayerChart
												class={mainLabel || device.type === 'SHGS-1'
													? 'mb-s'
													: ''}
												{device}
												{deviceChartsConfig}
												on:click={handleOnClickThumbChart}
											/>
										{/if}

										{#if mainLabel}
											{@const isCriticalEvent = checkIsCriticalEvent(
												mainLabel.value,
											)}
											<ParameterCard
												class={device.type === 'SHGS-1' ? 'mb-s' : ''}
												icon={mapIconToThin(mainLabel.icon)}
												title={capFirstChar(mainLabel.value)}
												description={capFirstChar(mainLabel.translation)}
												color={isCriticalEvent ? 'color-danger-20' : 'color-2'}
											/>
										{/if}

										{#if device.type === 'SHGS-1'}
											<ControlGasSensorMute {device} />
										{/if}
									</div>
								{/if}
							{:else if device.category === 'pv'}
								<div class="flex-full-height">
									<EnergyDistributionZendure {device} />
								</div>
							{:else if device.category === 'inputs_reader' || (device.category === 'module' && device.mode === 'inputs_reader') || (isXMODDevice(device?.type) && mode === 'inputs_reader')}
								<div class="flex-full-height justify-content-end">
									<AddonPeripheralsDrawer
										{isGen2}
										{device}
										on:click={handleOnClickThumbChart}
									/>
									<ControlsContainer>
										<InputsReaderVisualizer
											slot="first"
											{device}
											{status}
											{settings}
											isWide
											isInCard={false}
										/>
									</ControlsContainer>
								</div>
							{:else if isXMODDevice(device?.type) && (mode === 'relay' || mode === 'switch')}
								<div class="flex-full-height justify-content-end">
									<ControlOnOff {device} {status} {settings} isWide />
								</div>
							{:else if mode === 'roller'}
								<div class="flex-full-height justify-content-end">
									{#if rollerCalibrated && !rollerLostPosition && positioningControl}
										{#if isGen2}
											<ControlPositionG2 {device} {status} {settings} />
										{:else}
											<ControlPosition {device} {status} {settings} />
										{/if}
									{:else if hasAddonPeripherals(device)}
										<AddonPeripheralsDrawer
											{isGen2}
											{device}
											on:click={handleOnClickThumbChart}
										/>
									{/if}

									{#if isGen2}
										<ControlDirectionDevicePageG2 {device} {status} />
									{:else}
										<ControlDirectionDevicePage {device} {status} />
									{/if}
								</div>
							{:else if isFanDevice(device?.type)}
								<div
									class="flex-full-height justify-content-end d-flex flex-direction-column align-items-stretch gap-s min-height-0"
								>
									{#if status?.['fan:0']?.mode !== 'auto'}
										<div
											class="flex-full-height min-height-0 d-flex flex-direction-column"
										>
											<ControlVentoraFanSpeed {device} {status} />
										</div>
										<ControlOnOffProSensorSwitch {device} {status} {settings} />
									{:else}
										<ControlVentoraBoost
											{device}
											{status}
											isWide
											deviceButtonV2Props={{ size: 'l' }}
											class="ml-s"
										/>
									{/if}
								</div>
							{:else if mode === 'roller'}
								<div class="flex-full-height justify-content-end">
									{#if rollerCalibrated && !rollerLostPosition && positioningControl}
										{#if isGen2}
											<ControlPositionG2 {device} {status} {settings} />
										{:else}
											<ControlPosition {device} {status} {settings} />
										{/if}
									{:else if hasAddonPeripherals(device)}
										<AddonPeripheralsDrawer
											{isGen2}
											{device}
											on:click={handleOnClickThumbChart}
										/>
									{/if}

									{#if isGen2}
										<ControlDirectionDevicePageG2 {device} {status} />
									{:else}
										<ControlDirectionDevicePage {device} {status} />
									{/if}
								</div>
							{:else if device.category === 'thermostat' && !isWallDisplayVD}
								<div class="flex-full-height justify-content-end">
									<ControlTrvDevicePage
										{device}
										{status}
										{settings}
										current={labelList.find((el) => el.name === 'temperature')
											?.value}
									/>
									<ControlTrvBoost {device} {status} {settings} />
								</div>
							{:else if device.category === 'light' || mode === 'rgb' || mode === 'rgbw' || mode === 'diffuser' || mode === 'cct' || (isXMODDevice(device?.type) && ['light', 'cct', 'rgbw'].includes(device?.mode))}
								{#if selectedEffect > 0}
									<div class="mb-s flex-full-height justify-content-end">
										<Alert
											class="mb-s"
											heading={$format('alert_heading_non_calibrated_dimmer', {
												default: `Non calibrated channel.`,
											})}
											message={$format('description_dimmer_calibration', {
												default:
													"Calibrate current dimmer's channel. Note: During calibration you can't calibrate other channel. During calibration don't change current channel state.",
											})}
											isHeadingBold
											cta={{
												title: capFirstChar(
													$format('button_calibrate', {
														default: 'calibrate',
													}),
												),
											}}
											on:cta_click={() => {
												// console.warn(activeDevice);
												navigateToSetting(device.id, 'settings', 'calibration');
												// activeDeviceTabIndex = 14;
											}}
										/>
									</div>
								{:else if status?.[get_gen_2_component_channel_structure(device)]?.flags?.includes('uncalibrated') && _isDimmerWithCalibration}
									<div class="flex-full-height" />
									<Alert
										class="mb-s"
										heading={$format('alert_device_not_calibrated', {
											default: `Your device is not calibrated.`,
										})}
										message={$format('info_roller_calibrate_device', {
											default:
												'Please calibrate your device for the correct attitude!',
										})}
										isHeadingBold
										cta={{
											title: !isSubAccount()
												? capFirstChar(
														$format('button_calibrate', {
															default: 'calibrate',
														}),
													)
												: '',
										}}
										on:cta_click={() => {
											navigateToSetting(device.id, 'settings', 'calibration');
											// activeDeviceTabIndex = 14;
										}}
									/>
								{:else}
									{#if hasWhiteSlider}
										<div class="flex-full-height" />
										<ControlSlider
											{device}
											{status}
											{settings}
											prop={'white'}
											type="horizontal"
											needsRanging
										/>
										<!-- max is needed only for gen2's new white channel value limitation -->
									{/if}
									{#if isDali}
										{#if !noControlGearDevices && !daliErrors}
											<ControlSlider
												{device}
												{status}
												{settings}
												prop={sliderProp}
												type={hasWhiteSlider ? 'horizontal' : 'vertical'}
											/>
										{/if}
									{:else if isDiffuser(device.type)}
										<ControlCuryDrawer {device} {status} {settings} />
									{:else if sliderProp}
										<ControlSlider
											{device}
											{status}
											{settings}
											prop={sliderProp}
											type={hasWhiteSlider ? 'horizontal' : 'vertical'}
										/>
									{/if}
								{/if}

								{#if !noControlGearDevices && !daliErrors && !isDiffuser(device.type) && !isNoRelayPlug(device.type)}
									<!-- If device is not diffuser or dali, show the on/off control otherwise show the control component will be custom without the classic layout ordering-->
									<ControlOnOffProSensorSwitch {device} {status} {settings} />
								{/if}
							{:else if device.category === 'emeter' && !isSplit}
								<div class="lable-list-wrapper-in-drawer-tab-content mb-s">
									{#if emeter_timer}
										{#key status['relays'][0].timer_started}
											<TimerRemaining
												timerKey="gen1_emeter"
												{device}
												{status}
												{settings}
												bind:timerLabels
											/>
										{/key}
									{/if}
									{#if !isVirtualDevice(device?.type) && !isPeripheralDevice(device?.type) && !isXT1Module(device?.type) && device?.mode !== 'media_player' && !isWeatherStation(device.type)}
										<LabelList
											labelList={combinedLabelList}
											type="default"
											size="m"
											data-prevent-drawer-swipe
											data-prevent-sidepanel-swipe
											isDefaultCursor
											on:label-click={(e) => {
												e.stopPropagation();
											}}
										/>
									{/if}
								</div>

								{#if gen2}
									{#if isGen2EM(device.type) || device.mode === 'monophase'}
										<EmeterDataG2Phases {device} {status} />
									{:else}
										<EmeterDataG2 {device} {status} />
									{/if}
								{:else}
									<EmeterDataG1 {device} {status} />
								{/if}

								<PeakPowerInfo {device} />
								<div class="flex-full-height" />
								{#if deviceChartsConfig.length}
									<ThumbLayerChart
										{device}
										{deviceChartsConfig}
										on:click={handleOnClickThumbChart}
									/>
								{/if}
								{@const peripheral = getAddonStatus(device, [], false, true)}
								{#if !gen2}
									<ControlOnOff {device} {status} {settings} isWide />
								{/if}

								{@const emHasSwitch = 'switch:0' in status}
								{@const emHasSwitchAddon =
									peripheral && peripheral.find((i) => i.peripheralType == 'switch')}

								{#if emHasSwitchAddon && emHasSwitch}
									<div class="d-flex gap-s">
										<div class="control-flex-column">
											<ControlOnOffAddon
												{device}
												{status}
												isInSlot
												component={peripheral.find(
													(i) => i.peripheralType == 'switch',
												)?.sensor?.component}
											/>
										</div>
										<div class="control-flex-column">
											<ControlOnOff {device} {status} {settings} isInSlot />
										</div>
									</div>
								{:else if emHasSwitch && !emHasSwitchAddon}
									<ControlOnOff {device} {status} {settings} isWide />
								{:else if emHasSwitchAddon}
									<ControlOnOffAddon
										{device}
										{status}
										isWide
										component={peripheral.find((i) => i.peripheralType == 'switch')
											?.sensor?.component}
									/>
								{/if}
							{:else if isWallDisplay(device.type)}
								{#if !isWallDisplayVD}
									<div class="flex-full-height justify-content-end">
										{#if shouldUpdateConstantFirmwareVersion || wallDisplayUpdateNeeded}
											<div class="mb-s">
												<DeviceSettingsNewFw
													{device}
													values={{ fwVersion, minVersion }}
												/>
											</div>
										{/if}

										{#if !device?.hideCharts && deviceChartsConfig.length}
											<ThumbLayerChart
												{device}
												{deviceChartsConfig}
												on:click={handleOnClickThumbChart}
											/>
										{/if}

										{#if mainLabel}
											<ParameterCard
												icon={mainLabel.icon}
												iconColor={mainLabel?.iconColor || ''}
												title={capFirstChar(mainLabel.value)}
												description={capFirstChar(mainLabel.translation)}
											/>
										{:else}
											<ControlOnOff {device} {status} {settings} isWide />
										{/if}
									</div>
								{:else}
									<WallDisplayVd {device} />
								{/if}
							{:else}
								{#if isDeviceTemplatesSupported(device) && status?.online && !$devices[device.id]?.settingsAlertHandled}
									<Alert
										dismissable
										isHeadingBold
										class="mb-s"
										heading={$format('text_template_modal_title_apply', {
											default: 'Apply Settings Configuration',
										})}
										message={$format('text_template_modal_message_apply', {
											default:
												'You can apply settings either from existing device or pre-saved settings template.',
										})}
										cta={{
											title: capFirstChar(
												$format('button_apply', {
													default: 'Apply',
												}),
											),
										}}
										on:cta_click={() => {
											saveSettingsAlertState(device.id);
											showTemplatesModal = true;
										}}
										on:dismissable_click={() => saveSettingsAlertState(device.id)}
									/>{/if}
								<div class="flex-full-height justify-content-end">
									{#if hasAddonPeripherals(device) || hasExternalSensors(device)}
										<AddonPeripheralsDrawer
											{isGen2}
											{device}
											on:click={handleOnClickThumbChart}
										/>
									{:else if deviceChartsConfig.length}
										{#if minisStatisticsForceUpdateCheck(device, settings)}
											<UpdateRequiredStatistics {device} />
										{:else}
											<ThumbLayerChart
												{device}
												{deviceChartsConfig}
												on:click={handleOnClickThumbChart}
											/>
										{/if}
									{/if}
									{#if devices_config[device.type]?.isPM}
										<PeakPowerInfo {device} />
										<ParameterCard
											size="s"
											class="mb-s"
											icon="fas fa-plug"
											title={_statuses.filter((s) => s.name === 'power')[0]?.value}
											description={capFirstChar($format('collapse_title_power'))}
										/>
									{/if}
									{#if device.category !== 'gateway' && !isPeripheralDevice(device.type)}
										{#if !isActuator}
											{#if isLoqed}
												{#if mainLabel}
													<ParameterCard
														class="mb-s"
														size="s"
														icon={mainLabel.icon}
														iconColor={mainLabel.iconColor}
														title={capFirstChar(mainLabel.value)}
														description={capFirstChar(
															mainLabel.translation || '',
														)}
													/>
												{/if}
												<ControlLock {device} isWide />
											{:else if (!/^(virtual_device|module)$/.test(device.category) || (device.category === 'module' && device.mode !== 'shell') || (isXMODDevice(device?.type) && (status?.['switch:0'] || status?.['light:0'] || status?.['cct:0'] || status?.['rgbw:0']))) && !isNoRelayPlug(device.type)}
												<ControlOnOffProSensorSwitch
													{device}
													{status}
													{settings}
												/>
											{/if}
										{:else}
											<ParameterCard
												size="s"
												class="mb-s"
												icon="fas fa-thermometer"
												title={_statuses.filter(
													(s) => s.name === 'temperature',
												)[0]?.value}
												description={capFirstChar($format('base_temperature'))}
											>
												<svelte:fragment>
													{#if loadingStateWhenPending}
														<Loader />
													{/if}
												</svelte:fragment>
											</ParameterCard>
										{/if}
									{/if}
								</div>
							{/if}
						{:else}
							<div class="flex-full-height" />
						{/if}
					{:else if activeDevice.drawerTabItems[activeDeviceTabIndex]?.title === 'secondary_controls'}
						{@const isXMODCct =
							(isXMODDevice(device?.type) || isXT1ComponentDevice(device)) &&
							device?.mode === 'cct'}
						{@const isXMODRgbw =
							(isXMODDevice(device?.type) || isXT1ComponentDevice(device)) &&
							device?.mode === 'rgbw'}
						{@const isRgbwwPmCct = isRgbwwPm(device?.type) && device?.mode === 'cct'}
						<div
							class="secondary-controls-wrapper"
							class:disabled={secondaryControlsDisabled}
						>
							{#if !isXMODDevice(device?.type) && (hasColorControl || hasColorTempControl) && !lightDevicesWithoutModeSwitch(device.type)}
								<ControlMode
									{device}
									{settings}
									{mode}
									bind:disabled={secondaryControlsDisabled}
								/>
							{:else if hasColorControl && hasEffects}
								<ControlsContainer class="mb-m">
									<div slot="first">
										<ControlEffects {device} />
									</div>
								</ControlsContainer>
							{/if}

							{#if isXMODCct || isRgbwwPmCct || mode === 'white' || mode === 'cct' || (mode === 'rgbcct' && settings?.[`${mode}:0`]?.mode === 'cct')}
								<ControlTemp {device} {status} />
							{:else if isXMODRgbw || !isXMODDevice(device?.type)}
								<ControlColor {device} {status} />
							{/if}
						</div>
					{:else if activeDevice.drawerTabItems[activeDeviceTabIndex]?.title === 'ambient_light'}
						<ControlAmbientCury {device} {status} {settings} />
					{:else if chartTab}
						{#if minisStatisticsForceUpdateCheck(device, settings)}
							<UpdateRequiredStatistics {device} />
						{:else if device?.hideCharts}
							<div class="flex-full-height align-items-center justify-content-center">
								<CircleAvatar class="mb-l" size="xxl" icon="fa-thin fa-chart-line" />
								<Text element="h2" class="mb-s text-bold text-center">
									{capFirstChar(
										$format('alert_warning_charts_unavailable_heading', {
											default: 'Charts are currently unavailable',
										}),
									)}.
								</Text>
								<Text class="text-color-muted text-center" styleType="body-small">
									{$format('alert_warning_charts_unavailable_message', {
										default:
											'The charts are currently unavailable on this device, as an external sensor needs to be connected in order to display the data.',
									})}</Text
								>
							</div>
						{:else}
							{#if device?.category === 'charger'}
								<EvChargerLiveChart {device} />
							{/if}
							<DeviceChartsLayer {device} />
							{#if device?.category === 'charger'}
								<EvChargerLivetimeEnergy {device} />
							{/if}
						{/if}
					{:else if notificationsTab}
						<NotificationsPanel
							{device}
							on:edit_click={() => (showModalEditFromNotification = true)}
						/>
					{:else if eventsLogTab}
						{#if device?.category === 'charger'}
							<EvChargeEventLog {device} />
						{:else}
							<EventLog {device} type={'device'} {openedEventTimestamp} />
						{/if}
					{:else if thermostatTab}
						<ThermostatsG2 bind:device />
					{:else if mediaPlayerTab}
						<MediaPlayerComponent bind:device compact />
					{:else if cameraRecordingsTab}
						<CameraRecordingsComponent {device} />
					{:else if safetyTab}
						{#if DIMMERS_WITH_CALIBRATION.includes(device.type)}
							<Alert
								type="warning"
								heading={$format('voltage_protection_with_n_wire', {
									default:
										'Overpower, overvoltage and undervoltage protection works only when the device is used with Neutral wire.',
								})}
								class="mb-m"
							/>
						{/if}
						<DeviceSettingsWrapper
							{device}
							components={activeDevice.drawerTabItems[activeDeviceTabIndex]?.components}
							tab={activeDevice.drawerTabItems[activeDeviceTabIndex]?.title}
							shouldUpdateFWVersion={shouldUpdateConstantFirmwareVersion}
						/>
					{:else}
						{#if isCameraDevice(device) && activeDevice.drawerTabItems[activeDeviceTabIndex]?.title === 'camera_and_video'}
							<CameraStreamPreview {device} />
						{/if}
						{#if device?.gen === 'GBLE' && activeDevice.drawerTabItems[activeDeviceTabIndex]?.title === 'settings' && !device?.shared}
							{#if !$app}
								<Alert
									type="warning"
									isHeadingBold
									heading={$format('alert_heading_ble_devices_restricted', {
										default: 'Settings Restricted',
									})}
									message={$format('alert_message_ble_devices_restricted', {
										default:
											'Bluetooth devices can be configured only via the Shelly Smart Control application.',
									})}
									class="mb-m"
								/>
							{/if}

							{#if $app && !isSupportedVersion($os === 'IOS' ? '1.0.5' : '1.14.0')}
								<Alert
									type="warning"
									heading={$format('alert_heading_native_update_required', {
										default: 'App update required',
									})}
									message={$format('alert_message_native_update_required', {
										default:
											'Please update the application to the latest version for full support of these features.',
									})}
									class="mb-m"
								/>
							{/if}

							<BluetoothSettings {device} />
						{/if}
						<DeviceSettingsWrapper
							{device}
							components={activeDevice.drawerTabItems[activeDeviceTabIndex]?.components}
							tab={activeDevice.drawerTabItems[activeDeviceTabIndex]?.title}
							shouldUpdateFWVersion={shouldUpdateConstantFirmwareVersion}
						/>
					{/if}
				</DrawerTabContent>
			{/key}
		{/if}
	</DrawerTabs>
	<EditDeviceWizard
		{editDevice}
		bind:showModalEdit
		bind:showModalEditFromNotification
		on:finish={() => (comingLocation = $location)}
	/>
{/if}

{#if showTemplatesModal}
	<TemplatesModal bind:showModal={showTemplatesModal} {device} mode="select" />
{/if}

<style>
	.control-flex-column {
		flex: 1;
	}
</style>
